import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import {
  CreditCard,
  RefreshCw,
  Plus,
  Minus,
  Lock,
  Unlock,
  SlidersHorizontal,
  AlertTriangle,
  History,
  X,
  RotateCcw,
  Printer,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { buildCreditReprintReceipt, REPRINTABLE_CREDIT } from './reprintReceipt';

// ----------------------------------------------------------------
// CreditPanel — per-player house-credit ("marker") ledger.
// Rendered inside the Cage WalletPanel behind the Bank / Credit toggle.
// Talks to /admin/credit/*. Interest-free markers: a Draw increases what
// the player owes (hard-blocked above their limit); a Payment decreases it.
// ----------------------------------------------------------------

const fmtCents = (cents) => {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// Parse a dollar string to integer cents. Returns null if invalid.
const dollarsToCents = (s) => {
  if (s == null || String(s).trim() === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

const AGING_BUCKETS = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1–30 days' },
  { key: 'd31_60', label: '31–60 days' },
  { key: 'd61_90', label: '61–90 days' },
  { key: 'd90_plus', label: '90+ days' },
];

function creditEntryLabel(type) {
  switch (type) {
    case 'draw':
      return 'Credit draw';
    case 'payment':
      return 'Payment';
    case 'adjustment_increase':
      return 'Adjustment (increase)';
    case 'adjustment_decrease':
      return 'Adjustment (decrease)';
    case 'writeoff':
      return 'Write-off';
    case 'reversal_increase':
    case 'reversal_decrease':
      return 'Void / reversal';
    default:
      return type;
  }
}

export default function CreditPanel({
  userId,
  employeeNumber,
  setEmployeeNumber,
  activeSession,
  shiftsEnabled,
  onReprint,
}) {
  const { can } = useAuth();
  const [data, setData] = useState(null); // { account, transactions, aging }
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { kind, entryId? }

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/credit/players/${userId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load credit account');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-gray-400 flex items-center gap-3">
        <RefreshCw className="animate-spin" size={18} /> Loading credit account…
      </div>
    );
  }
  if (!data) return null;

  const { account, transactions, aging } = data;
  const items = transactions?.items || [];
  const buckets = aging?.buckets || {};
  const shiftBlocked = shiftsEnabled && !activeSession;
  const canOperate = can('credit:operate');
  const canManage = can('credit:manage');
  const canConfigure = can('credit:configure'); // enable credit + set limits (owner only)
  const canVoid = can('credit:void');

  const statusPill = (
    <span
      className={`px-2 py-0.5 rounded text-xs ${
        account.status === 'active'
          ? 'bg-emerald-500/10 text-emerald-400'
          : account.status === 'suspended'
            ? 'bg-amber-500/10 text-amber-400'
            : 'bg-gray-600 text-gray-300'
      }`}
    >
      {account.status}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* ---- Not-enabled empty state ---- */}
      {!account.creditEnabled ? (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
          <CreditCard className="mx-auto text-gray-500 mb-3" size={28} />
          <div className="text-white font-medium mb-1">Credit is not enabled</div>
          <div className="text-gray-400 text-sm mb-4">
            This player has no house credit line yet. A supervisor can enable
            credit and set a limit.
          </div>
          <button
            onClick={() => setModal({ kind: 'enable' })}
            disabled={!canConfigure || account.status === 'closed'}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Unlock size={16} /> Enable Credit
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* ---- Credit summary card ---- */}
          <div className="md:col-span-1 bg-gray-900 border border-gray-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="text-gray-400 text-sm uppercase tracking-wide">
                Balance owed
              </div>
              <button
                onClick={fetchData}
                className="text-gray-500 hover:text-amber-400"
                title="Refresh"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            <div className="text-3xl font-mono text-amber-400 mb-1">
              {fmtCents(account.balanceCents)}
            </div>
            <div className="text-xs text-gray-400 mb-3">
              of {fmtCents(account.creditLimitCents)} limit ·{' '}
              <span className={account.availableCents > 0 ? 'text-emerald-400' : 'text-red-400'}>
                {fmtCents(account.availableCents)} available
              </span>
            </div>

            <div className="flex items-center gap-2 mb-3">
              {statusPill}
              {account.overLimit && (
                <span className="px-2 py-0.5 rounded text-xs bg-red-500/10 text-red-400">
                  over limit
                </span>
              )}
            </div>

            {account.status === 'suspended' && account.suspendedReason && (
              <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{account.suspendedReason}</span>
              </div>
            )}

            {shiftBlocked && (
              <div className="mb-3 text-xs text-amber-400">
                Open a cage shift to post credit transactions.
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={() => setModal({ kind: 'draw' })}
                disabled={!canOperate || shiftBlocked || account.status !== 'active'}
                className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Draw Credit
              </button>
              <button
                onClick={() => setModal({ kind: 'payment' })}
                disabled={!canOperate || shiftBlocked || Number(account.balanceCents) <= 0}
                className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Minus size={16} /> Record Payment
              </button>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => setModal({ kind: 'limit' })}
                  disabled={!canConfigure}
                  className="px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <SlidersHorizontal size={14} /> Limit
                </button>
                {account.status === 'suspended' ? (
                  <button
                    onClick={() => setModal({ kind: 'reactivate' })}
                    disabled={!canManage}
                    className="px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    <Unlock size={14} /> Reactivate
                  </button>
                ) : (
                  <button
                    onClick={() => setModal({ kind: 'suspend' })}
                    disabled={!canManage}
                    className="px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                  >
                    <Lock size={14} /> Suspend
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ---- Aging + history ---- */}
          <div className="md:col-span-2 space-y-4">
            {/* Aging */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-gray-400 text-sm uppercase tracking-wide">
                  Aging of outstanding balance
                </div>
                {aging?.oldestDays > 0 && (
                  <div className="text-xs text-gray-500">
                    Oldest: {aging.oldestDays} day{aging.oldestDays === 1 ? '' : 's'}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {AGING_BUCKETS.map((b) => {
                  const cents = Number(buckets[b.key] || 0);
                  const hot = b.key === 'd61_90' || b.key === 'd90_plus';
                  return (
                    <div
                      key={b.key}
                      className="bg-gray-800 border border-gray-700 rounded-lg p-2 text-center"
                    >
                      <div className="text-[11px] text-gray-400 mb-1">{b.label}</div>
                      <div
                        className={`text-sm font-mono ${
                          cents > 0 ? (hot ? 'text-red-400' : 'text-white') : 'text-gray-600'
                        }`}
                      >
                        {fmtCents(cents)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between text-sm">
                <span className="text-gray-400">Total outstanding</span>
                <span className="font-mono text-amber-400">
                  {fmtCents(aging?.totalCents ?? account.balanceCents)}
                </span>
              </div>
            </div>

            {/* History */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
              <div className="flex items-center gap-2 text-gray-400 text-sm uppercase tracking-wide mb-3">
                <History size={14} /> Credit activity
              </div>
              {items.length === 0 ? (
                <div className="text-gray-500 text-sm">No credit transactions yet.</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {items.map((t) => {
                    const isReversal = t.entryType?.startsWith('reversal_');
                    const voidable = ['draw', 'payment', 'adjustment_increase', 'adjustment_decrease', 'writeoff'].includes(
                      t.entryType
                    );
                    return (
                      <div
                        key={t.id}
                        className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="text-white text-sm font-medium">
                            {creditEntryLabel(t.entryType)}
                            {t.banker && (
                              <span className="text-gray-500 font-normal"> by {t.banker.name}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {fmtTime(t.createdAt)}
                            {t.note ? ` · ${t.note}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span
                            className={`font-mono text-sm ${
                              t.direction === 'increase' ? 'text-red-400' : 'text-emerald-400'
                            }`}
                          >
                            {t.direction === 'increase' ? '+' : '−'}
                            {fmtCents(t.amountCents)}
                          </span>
                          {REPRINTABLE_CREDIT.has(t.entryType) && (
                            <button
                              onClick={() =>
                                onReprint?.(
                                  buildCreditReprintReceipt(t, {
                                    playerName:
                                      `${account?.user?.firstName || ''} ${account?.user?.lastName || ''}`.trim(),
                                    memberNumber: account?.memberNumber,
                                    creditLimitCents: account?.creditLimitCents,
                                  })
                                )
                              }
                              className="text-gray-600 hover:text-amber-400"
                              title="Reprint receipt"
                            >
                              <Printer size={14} />
                            </button>
                          )}
                          {canVoid && voidable && !isReversal && (
                            <button
                              onClick={() => setModal({ kind: 'void', entryId: t.id })}
                              className="text-gray-600 hover:text-red-400"
                              title="Void this entry"
                            >
                              <RotateCcw size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <CreditActionModal
          modal={modal}
          userId={userId}
          account={account}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onClose={() => setModal(null)}
          onDone={(updated) => {
            if (updated) setData(updated);
            else fetchData();
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// CreditActionModal — one modal for every credit action. Collects the
// amount / reason as needed plus employee # and PIN, then calls the API.
// ----------------------------------------------------------------
function CreditActionModal({
  modal,
  userId,
  account,
  employeeNumber,
  setEmployeeNumber,
  onClose,
  onDone,
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const CONFIG = {
    enable: {
      title: 'Enable Credit',
      cta: 'Enable Credit',
      role: 'Supervisor',
      amountLabel: 'Credit limit ($)',
      needsAmount: true,
      prefillAmount: account?.creditLimitCents ? account.creditLimitCents / 100 : '',
    },
    limit: {
      title: 'Adjust Credit Limit',
      cta: 'Save Limit',
      role: 'Supervisor',
      amountLabel: 'New credit limit ($)',
      needsAmount: true,
      prefillAmount: account?.creditLimitCents ? account.creditLimitCents / 100 : '',
    },
    draw: {
      title: 'Draw Credit',
      cta: 'Issue Marker',
      role: 'Banker',
      amountLabel: 'Draw amount ($)',
      needsAmount: true,
      needsNote: true,
      hint:
        account &&
        `Available credit: ${fmtCents(account.availableCents)} (limit ${fmtCents(
          account.creditLimitCents
        )}, owed ${fmtCents(account.balanceCents)})`,
    },
    payment: {
      title: 'Record Payment',
      cta: 'Record Payment',
      role: 'Banker',
      amountLabel: 'Payment amount ($)',
      needsAmount: true,
      needsNote: true,
      hint: account && `Outstanding balance: ${fmtCents(account.balanceCents)}`,
    },
    suspend: {
      title: 'Suspend Credit',
      cta: 'Suspend',
      role: 'Supervisor',
      needsReason: true,
      reasonLabel: 'Reason',
    },
    reactivate: {
      title: 'Reactivate Credit',
      cta: 'Reactivate',
      role: 'Supervisor',
    },
    void: {
      title: 'Void Credit Entry',
      cta: 'Void Entry',
      role: 'Supervisor',
      needsReason: true,
      reasonLabel: 'Reason for void',
    },
  };
  const cfg = CONFIG[modal.kind];

  // Prefill amount on first render for enable/limit.
  useEffect(() => {
    if (cfg.prefillAmount !== undefined && cfg.prefillAmount !== '') {
      setAmount(String(cfg.prefillAmount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!employeeNumber) return toast.error('Enter your employee number');
    if (!pin) return toast.error('PIN required');

    let amountCents = null;
    if (cfg.needsAmount) {
      amountCents = dollarsToCents(amount);
      if (amountCents == null) return toast.error('Enter a valid amount');
      if (modal.kind !== 'enable' && modal.kind !== 'limit' && amountCents <= 0) {
        return toast.error('Amount must be greater than zero');
      }
    }
    if (cfg.needsReason && !reason.trim()) return toast.error('Reason required');

    const auth = { employeeNumber, staffPin: pin };
    setSubmitting(true);
    try {
      let res;
      switch (modal.kind) {
        case 'enable':
          res = await api.post(`/admin/credit/players/${userId}/enable`, {
            creditLimitCents: amountCents,
            ...auth,
          });
          break;
        case 'limit':
          res = await api.put(`/admin/credit/players/${userId}/limit`, {
            creditLimitCents: amountCents,
            ...auth,
          });
          break;
        case 'draw':
          res = await api.post(`/admin/credit/players/${userId}/draw`, {
            amountCents,
            note: reason || undefined,
            ...auth,
          });
          break;
        case 'payment':
          res = await api.post(`/admin/credit/players/${userId}/payment`, {
            amountCents,
            note: reason || undefined,
            ...auth,
          });
          break;
        case 'suspend':
          res = await api.post(`/admin/credit/players/${userId}/status`, {
            status: 'suspended',
            reason,
            ...auth,
          });
          break;
        case 'reactivate':
          res = await api.post(`/admin/credit/players/${userId}/status`, {
            status: 'active',
            ...auth,
          });
          break;
        case 'void':
          res = await api.post(`/admin/credit/ledger/${modal.entryId}/void`, {
            reason,
            ...auth,
          });
          break;
        default:
          throw new Error('Unknown action');
      }
      toast.success(`${cfg.title} — done`);
      onDone(res?.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || `${cfg.title} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{cfg.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {cfg.hint && <div className="text-xs text-gray-400">{cfg.hint}</div>}

        {cfg.needsAmount && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">{cfg.amountLabel}</label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
        )}

        {(cfg.needsReason || cfg.needsNote) && (
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              {cfg.reasonLabel || 'Note'}
              {cfg.needsNote ? ' (optional)' : ''}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Employee #</label>
            <input
              type="text"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">{cfg.role} PIN</label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Requires a <span className="text-gray-300">{cfg.role.toLowerCase()}</span> cage PIN.
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50"
          >
            {submitting ? 'Working…' : cfg.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
